import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { useAppState, useDispatch } from 'state';
import styled from 'styled-components';

import { refreshTokens, signout } from 'actions/auth';
import useConfig from 'app/config';
import { routes } from 'app/routes/constants';
import useServices from 'app/services';
import { RequestError } from 'app/utils/requests/fetch';
import urlQueryParams from 'app/utils/requests/query-params';

import LoadingWithOverlay from 'app/ui/components/molecules/loading-with-overlay';
import ErrorComponent from 'app/ui/components/atoms/error';
import HtmlTitle from 'src/app/ui/components/atoms/html-title';

const Layout = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100vh;
  width: 100vw;
`;

const VerifyOneFiitPage = () => {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { config } = useConfig();
  const services = useServices();
  const dispatch = useDispatch();
  const minting = useRef(false);
  const [mintFailed, setMintFailed] = useState(false);

  const signedIn = useAppState(({ auth }) => auth.signedIn);
  const authorization = useAppState(({ auth }) => auth.authorization);
  const refreshToken = useAppState(({ auth }) => auth.refreshToken);
  const clientId = searchParams.get('client_id');
  const codeChallenge = searchParams.get('code_challenge');
  const codeChallengeMethod = searchParams.get('code_challenge_method');
  const redirectUri = searchParams.get('redirect_uri');
  const subpath = searchParams.get('subpath');
  const from = searchParams.get('from');
  const state = searchParams.get('state');
  const oneFiitMint = services.auth.oneFiitAuthCodeEnabled;

  const redirectToLogin = useCallback(
    (currentUrl: string) => {
      const redirectTo = encodeURIComponent(currentUrl);

      if (config.REACT_APP_SSO_ENABLED) {
        const loginUrl = `${config.REACT_APP_SSO_REDIRECT_URL}?verify_redirect_to=${redirectTo}`;
        window.location.replace(loginUrl);
      } else {
        const signinUrl = `${window.location.origin}${routes.SIGNIN}?verify_redirect_to=${redirectTo}`;
        window.location.replace(signinUrl);
      }
    },
    [config],
  );

  useEffect(() => {
    if (
      !oneFiitMint ||
      !signedIn ||
      !authorization ||
      minting.current ||
      !clientId ||
      !codeChallenge ||
      !codeChallengeMethod ||
      !redirectUri
    ) {
      return;
    }
    minting.current = true;

    const currentUrl = `${window.location.origin}${location.pathname}${location.search}`;

    const mint = (bearer: string) =>
      services.auth.mintDeepLinkAuthCode({
        authorization: bearer,
        clientId,
        redirectUri,
        codeChallenge,
        codeChallengeMethod,
        state: state || undefined,
      });

    const redirectWithCode = ({ authorizationCode }: { authorizationCode: string }) => {
      const target = new URL(redirectUri);
      target.searchParams.set('code', authorizationCode);
      if (state) {
        target.searchParams.set('state', state);
      }
      if (subpath) {
        target.searchParams.set('subpath', subpath);
      }
      if (from) {
        target.searchParams.set('from', from);
      }
      window.location.replace(target.toString());
    };

    const run = async () => {
      try {
        return redirectWithCode(await mint(authorization));
      } catch (error) {
        if (!(error instanceof RequestError) || error.status !== 401) {
          return setMintFailed(true);
        }
      }

      const refreshed = refreshToken ? await services.auth.refreshToken({ refreshToken }).catch(() => null) : null;

      if (!refreshed?.accessToken || !refreshed.refreshToken) {
        dispatch(signout());
        return redirectToLogin(currentUrl);
      }

      dispatch(refreshTokens(refreshed));
      try {
        redirectWithCode(await mint(`Bearer ${refreshed.accessToken}`));
      } catch {
        setMintFailed(true);
      }
    };

    run();
  }, [
    oneFiitMint,
    signedIn,
    authorization,
    refreshToken,
    services.auth,
    dispatch,
    redirectToLogin,
    clientId,
    codeChallenge,
    codeChallengeMethod,
    redirectUri,
    subpath,
    from,
    state,
    location.pathname,
    location.search,
  ]);

  if (!clientId || !codeChallenge || !codeChallengeMethod || !redirectUri) {
    return (
      <>
        <HtmlTitle title="Verify OneFiit - Error" />
        <Layout>
          <ErrorComponent details="Missing required parameters" />
        </Layout>
      </>
    );
  }

  if (!signedIn || !authorization) {
    setTimeout(() => {
      redirectToLogin(`${window.location.origin}${location.pathname}${location.search}`);
    }, 100);
    return (
      <>
        <HtmlTitle title="Verify OneFiit" />
        <Layout>
          <LoadingWithOverlay />
        </Layout>
      </>
    );
  }

  if (mintFailed) {
    return (
      <>
        <HtmlTitle title="Verify OneFiit - Error" />
        <Layout>
          <ErrorComponent details="Failed to authorize" />
        </Layout>
      </>
    );
  }

  if (!oneFiitMint) {
    // Construct URL with PKCE parameters to send to REACT_APP_AUTH_CODE_URL
    // This endpoint will exchange the PKCE params for an authorization code and redirect
    const params = urlQueryParams({
      client_id: clientId,
      code_challenge: codeChallenge,
      code_challenge_method: codeChallengeMethod,
      redirect_uri: redirectUri,
      authorization,
      subpath,
      from,
    });

    const authCodeUrl = `${config.REACT_APP_AUTH_CODE_URL}?${params}`;
    window.location.replace(authCodeUrl);
  }

  return (
    <>
      <HtmlTitle title="Verify OneFiit" />
      <Layout>
        <LoadingWithOverlay />
      </Layout>
    </>
  );
};

export default VerifyOneFiitPage;
